import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { DropDownList, DropDownListFilterChangeEvent } from "@progress/kendo-react-dropdowns";
import { useLocalization } from "@progress/kendo-react-intl";
import { enMessages } from "../../../../messages/en-US";
import type { TemplateSourceChoice } from "./useAssetTemplateDialogState";
import { filterBy } from "@progress/kendo-data-query";
import { Loader } from "@progress/kendo-react-indicators";

function getTemplateDisplayText(template: { name?: string; accountName?: string }): string {
  const name = template.name ?? "";
  const accountName = template.accountName?.trim();
  return accountName ? `${name} (${accountName})` : name;
}

interface TemplateSourceChoiceBlockProps {
  sourceOption: TemplateSourceChoice;
  onSelectNew: () => void;
  onSelectExisting: () => void;
  selectedExistingTemplateId: string | null;
  existingTemplates: any[] | undefined;
  loadingCopyTemplate: boolean;
  onExistingTemplateSelect: (templateId: string | null) => void;
}

export default function TemplateSourceChoiceBlock(
  props: TemplateSourceChoiceBlockProps
): ReactElement {
  const {
    sourceOption,
    onSelectNew,
    onSelectExisting,
    selectedExistingTemplateId,
    existingTemplates,
    loadingCopyTemplate,
    onExistingTemplateSelect,
  } = props;
  const localizationService = useLocalization();

  const templatesWithDisplayText = useMemo(() => {
    const list = existingTemplates ?? [];
    return list
      .map((t: any) => ({ ...t, displayText: getTemplateDisplayText(t) }))
      .sort((a: any, b: any) => (a.displayText ?? "").localeCompare(b.displayText ?? "", undefined, { sensitivity: "base" }));
  }, [existingTemplates]);

  const filterChange = (event: DropDownListFilterChangeEvent) => {
    const filter = { ...event.filter, field: "displayText" };
    setFilteredData(filterBy(templatesWithDisplayText, filter));
  };
  const [filteredData, setFilteredData] = useState(templatesWithDisplayText);

  useEffect(() => {
    setFilteredData(templatesWithDisplayText);
  }, [templatesWithDisplayText]);

  return (
    <div className="pb-4 border-b border-gray-200">
      <p className="gb-text-normal mb-3">
        {localizationService.toLanguageString(
          "custom.templateSourceChoice",
          enMessages.custom.templateSourceChoice
        )}
      </p>
      <ButtonGroup className="mb-3">
        <Button togglable selected={sourceOption === "new"} onClick={(e) => { e.preventDefault(); onSelectNew(); }}>
          {localizationService.toLanguageString("custom.newTemplate", enMessages.custom.newTemplate)}
        </Button>
        <Button togglable selected={sourceOption === "existing"} onClick={(e) => { e.preventDefault(); onSelectExisting(); }}>
          {localizationService.toLanguageString(
            "custom.copyFromExistingTemplate",
            enMessages.custom.copyFromExistingTemplate
          )}
        </Button>
      </ButtonGroup>
      {sourceOption === "existing" && (
        <div>
          <label className="block text-sm font-medium mt-4 mb-2">
            {localizationService.toLanguageString(
              "custom.selectTemplateToCopy",
              enMessages.custom.selectTemplateToCopy
            )}
          </label>
          <DropDownList
            data={filteredData}
            textField="displayText"
            dataItemKey="id"
            value={filteredData?.find((t: any) => t.id === selectedExistingTemplateId) ?? null}
            onChange={(e) => onExistingTemplateSelect(e.value?.id ?? null)}
            style={{ width: "100%", maxWidth: 400 }}
            disabled={loadingCopyTemplate}
            filterable={true}
            onFilterChange={filterChange}
          />
          {loadingCopyTemplate && (
            <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
              <Loader type="pulsing" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
